// The masterclass offer and the owner's card (C14, C18; Adam, 3 Oct: every line, both links, the
// minute and the offer-off switch are the owner's), and what the offer sells (catalogue.offer_product_id,
// the owner's too). Opened from the masterclass's card on Products ("Recording and offer"). An admin
// sees it read only. The database checks the shape and the links for every writer and refuses anyone
// but an owner (guard_masterclass_offer, guard_catalogue). No board draws it.
import { useState } from 'react'
import { Card, Button, Field, TextInput, TextArea, Select, Switch } from '../ui/index.js'
import { clock } from '../../lib/format.js'
import { OFFER } from './copy.js'
import { parseStamp, isHttps } from './model.js'
import { saveOffer, updateProduct } from './data.js'

const TEXT_KEYS = ['eyebrow', 'heading', 'body', 'join_label', 'join_url', 'secure_label', 'secure_url', 'balance_line', 'timing_line', 'placeholder_text', 'show_now_label']
const CARD_KEYS = ['title', 'line', 'button_label', 'button_url']
const lines = s => String(s || '').split('\n').map(x => x.trim()).filter(Boolean)

function formOf(offer, card, sells) {
  const o = offer || {}
  return {
    offer_on: o.offer_on !== false,
    at: Number.isFinite(o.at_seconds) ? clock(o.at_seconds) : '',
    sells: sells || '',
    stack: (o.stack || []).join('\n'),
    price_lines: (o.price_lines || []).join('\n'),
    ...Object.fromEntries(TEXT_KEYS.map(k => [k, o[k] ?? ''])),
    card: Object.fromEntries(CARD_KEYS.map(k => [k, card?.[k] ?? ''])),
  }
}

export function OfferEditor({ api, product, offer, products, owner, onSaved }) {
  const [f, setF] = useState(() => formOf(offer?.offer, offer?.owner_card, product.offer_product_id))
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState(null)
  const set = patch => { setAnswer(null); setF(s => ({ ...s, ...patch })) }
  const setCard = patch => { setAnswer(null); setF(s => ({ ...s, card: { ...s.card, ...patch } })) }
  const paid = (products || []).filter(p => p.access_kind === 'paid' && p.id !== product.id && p.type !== 'masterclass')

  async function save(e) {
    e.preventDefault()
    const errs = {}
    const at = parseStamp(f.at)
    if (at === null) errs.at = OFFER.atBad
    for (const k of ['join_url', 'secure_url']) if (f[k].trim() && !isHttps(f[k])) errs[k] = OFFER.urlBad
    if (f.card.button_url.trim() && !isHttps(f.card.button_url) && !/^\/\S*$/.test(f.card.button_url.trim())) errs.button_url = OFFER.cardUrlBad
    setErrors(errs)
    if (Object.keys(errs).length) return
    const nextOffer = {
      ...(offer?.offer || {}),
      offer_on: f.offer_on, at_seconds: at, stack: lines(f.stack), price_lines: lines(f.price_lines),
      ...Object.fromEntries(TEXT_KEYS.map(k => [k, f[k].trim() || null])),
    }
    const anyCard = CARD_KEYS.some(k => f.card[k].trim())
    const nextCard = anyCard ? Object.fromEntries(CARD_KEYS.map(k => [k, f.card[k].trim() || null])) : null
    setBusy(true)
    setAnswer(null)
    try {
      const row = await saveOffer(api, product.id, nextOffer, nextCard)
      let sells = product.offer_product_id
      if ((f.sells || null) !== (product.offer_product_id || null)) sells = (await updateProduct(api, product.id, { offer_product_id: f.sells || null })).offer_product_id
      onSaved(row, sells)
      setAnswer({ text: OFFER.saved })
    } catch (err) {
      setAnswer({ text: err.message, problem: true })
    } finally {
      setBusy(false)
    }
  }

  if (!owner) return <OfferReadOnly offer={offer} sells={(products || []).find(p => p.id === product.offer_product_id)} />

  const text = (k, label, opts = {}) => (
    <Field label={label} plain error={errors[k]} hint={opts.hint}>
      {p => (opts.area
        ? <TextArea {...p} rows={opts.rows || 3} value={f[k]} onChange={v => set({ [k]: v })} />
        : <TextInput {...p} type={opts.url ? 'url' : 'text'} value={f[k]} onChange={v => set({ [k]: v })} />)}
    </Field>
  )
  return (
    <Card title={OFFER.h2} aside={OFFER.aside} id="offer" className="ap-offer">
      <form className="ad-form" onSubmit={save} noValidate>
        <Switch checked={f.offer_on} onChange={v => set({ offer_on: v })} label={OFFER.on} stateLabel={`${OFFER.on}: ${OFFER.onState(f.offer_on)}`} />
        <div className="ad-row">
          {text('at', OFFER.at, { hint: OFFER.atHint })}
          <Field label={OFFER.sells} plain hint={OFFER.sellsHint}>
            {p => <Select {...p} value={f.sells} onChange={v => set({ sells: v })} placeholder={OFFER.sellsNone} options={paid.map(x => ({ value: x.id, label: x.name }))} />}
          </Field>
        </div>
        <div className="ad-row">{text('eyebrow', OFFER.eyebrow)}{text('heading', OFFER.heading)}</div>
        {text('body', OFFER.body, { area: true, rows: 4 })}
        {text('stack', OFFER.stack, { area: true, rows: 5 })}
        {text('price_lines', OFFER.prices, { area: true, rows: 4 })}
        <div className="ad-row">{text('join_label', OFFER.joinLabel)}{text('join_url', OFFER.joinUrl, { url: true })}</div>
        <div className="ad-row">{text('secure_label', OFFER.secureLabel)}{text('secure_url', OFFER.secureUrl, { url: true })}</div>
        {text('balance_line', OFFER.balance)}
        {text('timing_line', OFFER.timing)}
        {text('placeholder_text', OFFER.placeholder)}
        {text('show_now_label', OFFER.showNow)}
        <h3 className="ap-h3">{OFFER.cardH3}</h3>
        <div className="ad-row">
          <Field label={OFFER.cardTitle} plain>{p => <TextInput {...p} value={f.card.title} onChange={v => setCard({ title: v })} />}</Field>
          <Field label={OFFER.cardButton} plain>{p => <TextInput {...p} value={f.card.button_label} onChange={v => setCard({ button_label: v })} />}</Field>
        </div>
        <Field label={OFFER.cardLine} plain>{p => <TextInput {...p} value={f.card.line} onChange={v => setCard({ line: v })} />}</Field>
        <Field label={OFFER.cardUrl} plain hint={OFFER.cardUrlHint} error={errors.button_url}>{p => <TextInput {...p} value={f.card.button_url} onChange={v => setCard({ button_url: v })} />}</Field>
        <div className="ap-save-row">
          <Button kind="ink" type="submit" busy={busy}>{OFFER.save}</Button>
          {answer && <p className={answer.problem ? 'ad-error' : 'ap-saved'} role={answer.problem ? 'alert' : 'status'}>{answer.text}</p>}
        </div>
      </form>
    </Card>
  )
}

function OfferReadOnly({ offer, sells }) {
  const o = offer?.offer || {}
  const c = offer?.owner_card || {}
  const show = v => (Array.isArray(v) ? (v.length ? v.join(' / ') : OFFER.empty) : (v === null || v === undefined || v === '' ? OFFER.empty : String(v)))
  const rows = [
    [OFFER.on, OFFER.onState(o.offer_on !== false)],
    [OFFER.at, Number.isFinite(o.at_seconds) ? clock(o.at_seconds) : OFFER.none],
    [OFFER.sells, sells?.name || OFFER.sellsNone],
    [OFFER.eyebrow, show(o.eyebrow)], [OFFER.heading, show(o.heading)], [OFFER.body, show(o.body)],
    [OFFER.stack, show(o.stack)], [OFFER.prices, show(o.price_lines)],
    [OFFER.joinLabel, show(o.join_label)], [OFFER.joinUrl, show(o.join_url)],
    [OFFER.secureLabel, show(o.secure_label)], [OFFER.secureUrl, show(o.secure_url)],
    [OFFER.balance, show(o.balance_line)], [OFFER.timing, show(o.timing_line)],
    [OFFER.placeholder, show(o.placeholder_text)], [OFFER.showNow, show(o.show_now_label)],
    [`${OFFER.cardH3}: ${OFFER.cardTitle}`, show(c.title)], [`${OFFER.cardH3}: ${OFFER.cardLine}`, show(c.line)],
    [`${OFFER.cardH3}: ${OFFER.cardButton}`, show(c.button_label)], [`${OFFER.cardH3}: ${OFFER.cardUrl}`, show(c.button_url)],
  ]
  return (
    <Card title={OFFER.h2} aside={OFFER.aside} id="offer" className="ap-offer">
      <p className="ad-lede">{OFFER.adminNote}</p>
      <dl className="ap-facts">
        {rows.map(([k, v]) => <div className="ap-fact" key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
    </Card>
  )
}
